import React, {FC, useLayoutEffect, useRef, useState} from "react";
import tabStyles from "@/styles/Tabs.module.css";
import {Box, Button, Flex, Menu, Tabs, TabsProps, Text} from "@mantine/core";
import {useElementSize} from "@mantine/hooks";
import {IconDots} from "@tabler/icons-react";
import NewText from "@/PageComponents/Premium/NewText";

interface TabProps {
  text: string;
  fitContent?: boolean;
  suppressCount?: boolean;
  count?: number;
  disabled?: boolean;
  newItem?: boolean;
}

// Room kept for the "more tabs" button when some tabs don't fit
const MORE_BUTTON_WIDTH = 64;

// inMenu: outside a tab the counter needs its own background (tab styles colour it otherwise)
const TabCounter: FC<{count?: number; inMenu?: boolean}> = ({count, inMenu}) =>
    <Flex
        justify={'center'}
        align={'center'}
        className={tabStyles.counter}
        color={'var(--mantine-color-scBlue-5)'}
        bg={inMenu ? 'gray.5' : undefined}
        w={18} h={18} style={{borderRadius: '50%'}}
    >
      <Text ta={'center'} size={'10px'} fw={600} c={'white'} mx={'auto'}>
        {count}
      </Text>
    </Flex>

const showCount = (tab: TabProps, i: number, showTab1Count?: boolean) => (i !== 0 || showTab1Count) && !tab.suppressCount

const PageTabs:FC<{tabs: TabProps[]; setSelectedTab: (text: string | null) => void; selectedTab?: string | null; showTab1Count?: boolean; tabsProps: TabsProps; overflowMenu?: boolean; onTotalWidth?: (width: number) => void}> =
    (props: any) => {

  const tabContent = (tab: any, i: number) =>
      <Flex gap={10} align={'center'}>
        {tab.text} {tab.newItem && <NewText ml={-6} />}
        {showCount(tab, i, props.showTab1Count) && <TabCounter count={tab.count} />}
      </Flex>

  // Overflow: measure every tab off-screen, then show the ones that fit and move the rest into a menu.
  // The selected tab always stays visible.
  const {ref: containerRef, width: containerWidth} = useElementSize();
  const measureRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(props.tabs.length);

  useLayoutEffect(() => {
    if (!props.overflowMenu || !containerWidth) return;
    const widths = props.tabs.map((_: any, i: number) => measureRefs.current[i]?.offsetWidth ?? 0);
    const total = widths.reduce((a: number, b: number) => a + b, 0);
    // lets the page make room for every tab when it can
    props.onTotalWidth?.(total);
    if (total <= containerWidth) {
      setVisibleCount(props.tabs.length);
      return;
    }
    let used = MORE_BUTTON_WIDTH, count = 0;
    while (count < widths.length && used + widths[count] <= containerWidth) {
      used += widths[count];
      count++;
    }
    setVisibleCount(Math.max(1, count));
  }, [props.overflowMenu, containerWidth, props.tabs]);

  const indexed = props.tabs.map((tab: any, i: number) => ({tab, i}));
  let visible = props.overflowMenu ? indexed.slice(0, visibleCount) : indexed;
  let hidden = props.overflowMenu ? indexed.slice(visibleCount) : [];
  const selectedHidden = hidden.find((x: any) => x.tab.text === props.selectedTab);
  if (selectedHidden && visible.length > 0) {
    const bumped = visible[visible.length - 1];
    visible = [...visible.slice(0, -1), selectedHidden];
    hidden = [bumped, ...hidden.filter((x: any) => x !== selectedHidden)];
  }

  return <>
    <Box ref={containerRef} pos={'relative'} miw={0} w={'100%'}>
      <Tabs color={'scBlue'} value={props.selectedTab} onChange={props.setSelectedTab}
            classNames={{
              tab: tabStyles.scTab,
              list: tabStyles.scTabList,
              tabLabel: tabStyles.scTabLabel
            }}
            {...props.tabsProps}
      >
        <Tabs.List mb={0} pb={0} style={{ flexWrap: props.overflowMenu ? 'nowrap' : 'wrap' }}>
          {visible.map(({tab, i}: any) =>
              <Tabs.Tab value={tab.text}
                        disabled={tab.disabled}
                        key={'main-tab-' + i}
              >
                {tabContent(tab, i)}
              </Tabs.Tab>
          )}
          {hidden.length > 0 &&
              <Menu position={'bottom-end'} shadow={'md'} width={220}>
                <Menu.Target>
                  {/* the tabs carry extra bottom padding (12px), so the 8px bottom margin lifts this to the middle of the tab labels and the buttons beside them */}
                  <Button variant={'default'} size={'compact-sm'} h={30} px={8} ml={4} mb={8} miw={0} style={{alignSelf: 'center', flexShrink: 0}}
                          aria-label={'More tabs'} leftSection={<IconDots size={18} />}>
                    {hidden.length}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {hidden.map(({tab, i}: any) =>
                      <Menu.Item key={'more-tab-' + i} disabled={tab.disabled} onClick={() => props.setSelectedTab(tab.text)}
                                 rightSection={showCount(tab, i, props.showTab1Count) ? <TabCounter count={tab.count} inMenu /> : undefined}>
                        {tab.text}
                      </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
          }
        </Tabs.List>
      </Tabs>

      {props.overflowMenu &&
          <Box aria-hidden pos={'absolute'} top={0} left={0} h={0} style={{visibility: 'hidden', overflow: 'hidden', pointerEvents: 'none'}}>
            <Tabs value={props.selectedTab} classNames={{tab: tabStyles.scTab, list: tabStyles.scTabList, tabLabel: tabStyles.scTabLabel}} {...props.tabsProps}>
              <Tabs.List style={{flexWrap: 'nowrap', width: 'max-content'}}>
                {props.tabs.map((tab: any, i: number) =>
                    <Tabs.Tab value={'measure-' + tab.text} key={'measure-tab-' + i} tabIndex={-1}
                              ref={(el: HTMLButtonElement | null) => { measureRefs.current[i] = el; }}>
                      {tabContent(tab, i)}
                    </Tabs.Tab>
                )}
              </Tabs.List>
            </Tabs>
          </Box>
      }
    </Box>
  </>
}

export default PageTabs
